import React, { useMemo } from "react";
import ReactApexChart from "react-apexcharts";
import "./styles/dashboard.css";

const SalesOverviewGraph = ({ series = [], labels = [] }) => {
  // Calculate dynamic y-axis max based on data
  const yAxisMax = useMemo(() => {
    if (!series || series.length === 0) return 32;
    
    let maxValue = 0;
    series.forEach((s) => {
      if (s.data && Array.isArray(s.data)) {
        const seriesMax = Math.max(...s.data.map((val) => val || 0));
        maxValue = Math.max(maxValue, seriesMax);
      }
    });
    
    // Add 20% padding to the top and round up to nearest 5
    const paddedMax = maxValue * 1.2;
    return Math.ceil(paddedMax / 5) * 5 || 32;
  }, [series]);

  // Determine if we need horizontal scrolling (more than 7 data points)
  const needsScrolling = labels.length > 7;
  
  // Calculate dynamic bar width based on number of data points
  // For many data points, use a minimum width to keep bars visible
  const barWidth = useMemo(() => {
    if (!needsScrolling) return "40px";
    // For scrolling: ensure bars are at least 50px wide for visibility
    // Don't scale down too much - keep bars readable
    const minBarWidth = 50;
    return `${minBarWidth}px`;
  }, [needsScrolling]);

  // Calculate chart width for scrolling
  // Ensure each bar has enough space and total width exceeds container
  const chartWidthNumeric = useMemo(() => {
    if (!needsScrolling) return null;
    // Minimum 50px per bar, plus spacing
    const widthPerBar = 60; // 50px bar + 10px spacing
    const calculatedWidth = labels.length * widthPerBar;
    // Ensure minimum width to trigger scrolling - make it definitely wider than container
    // For a typical container width of ~800-1000px, ensure chart is wider
    return Math.max(calculatedWidth, 1500);
  }, [needsScrolling, labels.length]);
  
  // Chart width as string for CSS and ApexCharts
  const chartWidthString = useMemo(() => {
    if (!needsScrolling) return "100%";
    return `${chartWidthNumeric}px`;
  }, [needsScrolling, chartWidthNumeric]);

  const options = {
      chart: {
      height: 300,
      type: "line",
      width: chartWidthString,
      toolbar: {
        show: false,
      },
      zoom: {
        enabled: false, // Disable ApexCharts zoom to use native scrollbar
        type: "x",
        autoScaleYaxis: false,
      },
      scrollbar: {
        enabled: false, // Disable ApexCharts scrollbar, use native browser scrollbar
      },
    },
    stroke: {
      width: [0, 2],
      curve: "smooth",
    },
    markers: {
      size: [0, 6],
      colors: ["#FFFFFF"],
      strokeColors: "#EF4444",
      strokeWidth: 2,
      hover: {
        size: 7,
      },
    },
    plotOptions: {
      bar: {
        borderRadius: 6,
        columnWidth: barWidth,
        barHeight: "100%",
      },
    },
    colors: ["#3B82F6", "#EF4444"],
    dataLabels: {
      enabled: false,
    },
    labels: labels,
    xaxis: {
      labels: {
        style: {
          colors: "#6B7280",
          fontSize: "12px",
          fontWeight: 500,
        },
        rotate: needsScrolling ? -45 : 0,
        rotateAlways: needsScrolling,
        maxHeight: needsScrolling ? 60 : undefined,
      },
      tickAmount: needsScrolling ? undefined : 7,
    },
    yaxis: {
      min: 0,
      max: yAxisMax,
      tickAmount: 5,
      labels: {
        style: {
          colors: "#9CA3AF",
          fontSize: "12px",
        },
        formatter: (value) => {
          return value.toFixed(1);
        },
      },
      forceNiceScale: true,
    },
    grid: {
      borderColor: "#E5E7EB",
      strokeDashArray: 5,
      padding: {
        top: 10,
        right: needsScrolling ? 15 : 0,
        bottom: needsScrolling ? 50 : 10,
        left: 10,
      },
    },
    legend: {
      show: false,
    },
  };

  return (
    <div 
      style={{ 
        padding: "0",
        width: "100%",
        overflowX: needsScrolling ? "scroll" : "visible",
        overflowY: "visible",
        position: "relative",
        maxWidth: "100%",
        height: "300px",
      }}
      className="dashboard-chart-container"
    >
      <div style={{ 
        width: chartWidthString,
        minWidth: chartWidthString,
        display: "block",
        position: "relative",
      }}>
        <ReactApexChart
          options={options}
          series={series}
          type="line"
          height={needsScrolling ? "350px" : "300px"}
          width={chartWidthString}
        />
      </div>
    </div>
  );
};

export default SalesOverviewGraph;
